import * as Sentry from '@sentry/react'

import { Auth } from '../types/common/httpRequest'
import { apiService, DeviceApiNetworkError } from './apiService'

/**
 * localStorage marker: set on first successful registration and never cleared
 * by the app (only by debug-menu `localStorage.clear()`). Survives OS wipes
 * of the device token key alone. Together with `Auth.POS_token` it tells us
 * this kiosk has been registered before.
 */
const REGISTRATION_MARKER_KEY = 'vcash_device_was_registered'

export type DeviceTokenBootstrapResult =
    | 'ready'
    | 'connectivity_retry'
    | 'register'

/**
 * Device Token Service
 *
 * Token resolution order (best of both worlds):
 *  1. Valid token in localStorage → use it (fast path; OS may have wiped it
 *     but we still have the marker + device API as backup).
 *  2. No token in localStorage → load from device API; if found, hydrate
 *     localStorage + marker.
 *  3. Device API unreachable OR (was registered before but API returned
 *     empty) → connectivity-issues; keep retrying. Never /register.
 *  4. /register ONLY when: no token, no marker, AND device API responded
 *     successfully with an empty token (genuinely unregistered device).
 */
class DeviceTokenService {
    private initialized = false
    private localApiNetworkError = false
    /** Device API returned HTTP success but no token (not a network failure). */
    private localApiRespondedEmpty = false

    /**
     * Bootstrap token state once at app startup. Callers use the result for
     * routing instead of re-deriving flags.
     */
    async initialize(): Promise<DeviceTokenBootstrapResult> {
        if (this.initialized) {
            return this.getBootstrapResult()
        }

        this.localApiNetworkError = false
        this.localApiRespondedEmpty = false

        try {
            if (this.hasTokenInLocalStorage()) {
                this.markAsRegistered()
                console.log(
                    'Device token present in localStorage - using cached token (device API available as backup)'
                )
                this.initialized = true
                return 'ready'
            }

            console.log(
                'No device token in localStorage - loading from device API...'
            )
            const deviceToken = await apiService.getDeviceToken()

            if (deviceToken) {
                this.persistDeviceToken(deviceToken)
                console.log(
                    'Device token loaded from device API and persisted to localStorage'
                )
                this.initialized = true
                return 'ready'
            }

            this.localApiRespondedEmpty = true
            console.log(
                'Device API responded with no token (HTTP success, empty body)'
            )

            if (this.hasBeenRegistered()) {
                console.warn(
                    'Device API has no token but localStorage shows prior registration (token or marker) - treating as recoverable, not /register'
                )
                Sentry.captureMessage(
                    'Device API returned empty token for kiosk with prior registration in localStorage',
                    {
                        level: 'warning',
                        tags: {
                            scope: 'deviceTokenService.initialize',
                            local_api: 'empty_with_prior_registration'
                        }
                    }
                )
                this.initialized = true
                return 'connectivity_retry'
            }

            console.log(
                'No token in localStorage or device API, and no prior registration marker - device needs registration'
            )
            this.initialized = true
            return 'register'
        } catch (error) {
            if (error instanceof DeviceApiNetworkError) {
                this.localApiNetworkError = true
                console.warn(
                    'Local device API unreachable while loading token - will retry on connectivity-issues page:',
                    error
                )
                Sentry.captureException(error, {
                    tags: {
                        scope: 'deviceTokenService.initialize',
                        local_api: 'unreachable'
                    }
                })
                this.initialized = true
                return 'connectivity_retry'
            }

            console.warn(
                'Error loading device token from device API, keeping localStorage intact:',
                error
            )
            this.initialized = true
            return this.getBootstrapResult()
        }
    }

    async saveDeviceToken(token: string): Promise<boolean> {
        try {
            this.persistDeviceToken(token)

            const success = await apiService.saveDeviceToken(token)

            if (success) {
                console.log('Device token saved to persistent storage')
            } else {
                console.warn(
                    'Failed to save device token to persistent storage'
                )
            }

            this.localApiNetworkError = false
            this.localApiRespondedEmpty = false

            return success
        } catch (error) {
            console.error('Error saving device token:', error)
            return false
        }
    }

    hasLocalApiNetworkError(): boolean {
        return this.localApiNetworkError
    }

    hasLocalApiRespondedEmpty(): boolean {
        return this.localApiRespondedEmpty
    }

    /**
     * @deprecated Prefer `shouldNavigateToRegister()` for routing decisions.
     * True when localStorage shows prior registration but device API had no token.
     */
    hasLocalApiTokenLikelyLost(): boolean {
        return (
            this.hasBeenRegistered() &&
            !this.hasTokenInLocalStorage() &&
            !this.localApiNetworkError
        )
    }

    /** True only for genuinely unregistered devices (no token, no marker, API empty). */
    shouldNavigateToRegister(): boolean {
        return (
            this.localApiRespondedEmpty &&
            !this.hasBeenRegistered() &&
            !this.localApiNetworkError
        )
    }

    shouldNavigateToConnectivityIssues(): boolean {
        return this.getBootstrapResult() === 'connectivity_retry'
    }

    /**
     * Reload token from device API (connectivity page + recovery). Updates
     * flags so routing stays consistent after reload.
     */
    async reloadFromLocalApi(): Promise<string> {
        try {
            const deviceToken = await apiService.getDeviceToken()
            this.localApiNetworkError = false

            if (deviceToken) {
                this.persistDeviceToken(deviceToken)
                this.localApiRespondedEmpty = false
                console.log(
                    'Device token reloaded from local API and stored in localStorage'
                )
                return deviceToken
            }

            this.localApiRespondedEmpty = true
            console.log(
                'Device API reload returned no token (HTTP success, empty body)'
            )
            return ''
        } catch (error) {
            if (error instanceof DeviceApiNetworkError) {
                this.localApiNetworkError = true
                Sentry.captureException(error, {
                    tags: {
                        scope: 'deviceTokenService.reloadFromLocalApi',
                        local_api: 'unreachable'
                    }
                })
            }
            throw error
        }
    }

    hasBeenRegistered(): boolean {
        return (
            this.hasTokenInLocalStorage() ||
            localStorage.getItem(REGISTRATION_MARKER_KEY) === 'true'
        )
    }

    isInitialized(): boolean {
        return this.initialized
    }

    private getBootstrapResult(): DeviceTokenBootstrapResult {
        if (!this.hasTokenInLocalStorage()) {
            if (this.localApiNetworkError) {
                return 'connectivity_retry'
            }
            if (this.shouldNavigateToRegister()) {
                return 'register'
            }
            if (this.hasBeenRegistered()) {
                return 'connectivity_retry'
            }
            return 'register'
        }
        return 'ready'
    }

    private hasTokenInLocalStorage(): boolean {
        return !!localStorage.getItem(`${Auth.POS}_token`)
    }

    private persistDeviceToken(token: string): void {
        localStorage.setItem(`${Auth.POS}_token`, token)
        this.markAsRegistered()
    }

    private markAsRegistered(): void {
        if (localStorage.getItem(REGISTRATION_MARKER_KEY) !== 'true') {
            localStorage.setItem(REGISTRATION_MARKER_KEY, 'true')
        }
    }
}

export const deviceTokenService = new DeviceTokenService()
